import React, { useState, useCallback, useEffect } from 'react';
import { Modal, Form, Button, Checkbox, Input } from 'tea-component';
import TinyEditor from '@sg/components/TinyEditor';
import AsyncTagSelect from '@sg/components/AsyncTagSelect';
import { getUserList } from '@sg/services';
import './index.scss';

interface ShareMeetingModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (data: any) => void;
  meetingData: any | null;
}

interface IUserOption extends IGPSelectOptionWithGroup {
  text: string;
  value: string;
  user_id: string;
  user_name: string;
  email?: string;
}

interface IShareMeetingFormModel {
  shareVia: {
    email: boolean;
    weCom: boolean;
  };
  selectedUsers: string[];
  title: string;
  body: string;
}

const DEFAULT_FORM_MODEL: IShareMeetingFormModel = {
  shareVia: {
    email: false,
    weCom: false,
  },
  selectedUsers: [],
  title: '',
  body: '',
};

const ShareMeetingModal: React.FC<ShareMeetingModalProps> = ({
  visible,
  onClose,
  onConfirm,
  meetingData,
}) => {
  const [editorKey, setEditorKey] = useState(0);
  const [formModel, setFormModel] = useState<IShareMeetingFormModel>(DEFAULT_FORM_MODEL);
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (visible) {
      setEditorKey((prev) => prev + 1);
    }
  }, [visible]);

  const handleBodyChange = useCallback((htmlContent: string) => {
    setFormModel((prev) => ({ ...prev, body: htmlContent }));
  }, []);

  const fetchUserOptions = useCallback(async (keyword: string): Promise<IUserOption[]> => {
    const response = await getUserList({ user_name: keyword });
    const userData = response?.data?.data || response?.data || response;

    if (!Array.isArray(userData) || userData.length === 0) {
      return [];
    }

    return userData
      .filter((user: any) => user?.user_id)
      .map((user: any) => ({
        text: `${user.user_name} (${user.email || '-'})`,
        value: String(user.user_id),
        user_id: String(user.user_id),
        user_name: user.user_name,
        email: user.email,
      }));
  }, []);

  const handleClose = () => {
    setFormModel(DEFAULT_FORM_MODEL);
    setIsSending(false);
    onClose();
  };

  const handleConfirm = async () => {
    const shareTypes: string[] = [];
    if (formModel.shareVia.email) shareTypes.push('email');
    if (formModel.shareVia.weCom) shareTypes.push('wecom');

    const shareData = {
      meetingId: meetingData?.id,
      shareVia: formModel.shareVia,
      share_types: shareTypes,
      user_id: formModel.selectedUsers,
      title: formModel.title,
      body: formModel.body,
      meetingData,
    };

    try {
      setIsSending(true);
      await onConfirm(shareData);
      // Only close modal if API call succeeds
      handleClose();
    } catch (error) {
      console.error('Failed to share meeting:', error);
      // Keep modal open on error, user can retry
    } finally {
      setIsSending(false);
    }
  };

  const isFormValid = () => {
    const hasShareMethod = formModel.shareVia.email || formModel.shareVia.weCom;
    const hasRecipients = formModel.selectedUsers.length > 0;
    const hasTitle = formModel.title.trim().length > 0;
    return hasShareMethod && hasRecipients && hasTitle;
  };

  if (!meetingData) return null;

  return (
    <Modal
      visible={visible}
      onClose={handleClose}
      caption="Share Meeting"
      size="l"
      className="dbs-share-meeting-modal dbs-common-modal"
      disableCloseIcon={isSending}
    >
      <Modal.Body>
        <form autoComplete="off" onSubmit={(event) => event.preventDefault()}>
          <input
            type="text"
            name="dbs-share-meeting-fake-user"
            autoComplete="username"
            style={{ display: 'none' }}
          />
          <input
            type="password"
            name="dbs-share-meeting-fake-password"
            autoComplete="new-password"
            style={{ display: 'none' }}
          />

          <Form>
            <Form.Item label="Share via" required>
              <Checkbox.Group
                value={[
                  ...(formModel.shareVia.email ? ['email'] : []),
                  ...(formModel.shareVia.weCom ? ['weCom'] : []),
                ]}
                onChange={(values) => {
                  setFormModel((prev) => ({
                    ...prev,
                    shareVia: {
                      email: values.includes('email'),
                      weCom: values.includes('weCom'),
                    },
                  }));
                }}
              >
                <Checkbox name="email">Email</Checkbox>
                <Checkbox name="weCom">WeCom</Checkbox>
              </Checkbox.Group>
            </Form.Item>

            <Form.Item label="Recipients" required>
              <AsyncTagSelect
                optionsOnly
                // className="dbs-share-meeting-modal__recipients"
                // overlayClassName="dbs-share-meeting-modal__recipients-overlay"
                placeholder="Search by user name"
                value={formModel.selectedUsers}
                fetchOptions={fetchUserOptions}
                strictSubstringMatch={true}
                onChange={(value) => {
                  setFormModel((prev) => ({ ...prev, selectedUsers: value || [] }));
                }}
                statusTipProps={{
                  status: 'loading',
                  // emptyText: 'Search by user name',
                  loadingText: 'Loading...',
                  errorText: 'Failed to fetch users. Please try again.',
                }}
              />
            </Form.Item>

            <Form.Item label="Title" required>
              <Input
                value={formModel.title}
                onChange={(value) => setFormModel((prev) => ({ ...prev, title: value }))}
                placeholder="Please enter"
              />
            </Form.Item>

            <Form.Item label="Body">
              <TinyEditor
                key={`editor-${editorKey}`}
                value={formModel.body}
                getEditorContent={handleBodyChange}
                placeholder="Please enter"
                minHeight={200}
                maxContentHeight={300}
                isMaxHeightScroll={true}
                isShowToolbar={true}
                showCharacterCount={false}
              />
            </Form.Item>
          </Form>
        </form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="primary"
          onClick={handleConfirm}
          disabled={!isFormValid() || isSending}
          loading={isSending}
          className="dbs-gradient-btn"
        >
          Send
        </Button>
        <Button onClick={handleClose} disabled={isSending}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ShareMeetingModal;
